<template>
  <a-table
    @change="handleTableChange"
    size="middle"
    rowKey="uuid"
    :pagination="pagination"
    :loading="fetching"
    :columns="columnsConfig"
    :dataSource="results"
    class="table"
  >
    <div
      class="table__date"
      slot="ordered_at"
      slot-scope="date"
    >
      <div>{{ $moment(date).format("DD.MM.YYYY") }}</div>
      <div class="table__datetime">
        {{ $moment(date).format("HH:mm") }}
      </div>
    </div>

    <div
      class="table__offer"
      slot="offer"
      slot-scope="offer"
    >
      <Thumbnail
        @click.native.stop
        class="table__offer-thumbnail"
        :images="offer.image ? [offer.image] : []"
        :size="40"
        hideCount
      />
      <div class="table__offer-description-wrapper">
        <div>{{ offer.title }}</div>
        <OfferCode
          @click.native.stop
          :slug="offer.slug"
          :offerCode="offer.cs_code"
          :productCode="offer.p_code"
        />
      </div>
    </div>

    <div
      class="table__status"
      slot="status"
      slot-scope="modStatus, row"
    >
      <div>{{ getModStatusLabel(modStatus) }}</div>
      <div
        v-if="modStatus !== 'PENDING'"
        class="table__status-date"
      >
        {{ $moment(row.cpa.expenses_date).format("DD.MM.YYYY") }}
      </div>
    </div>
  </a-table>
</template>

<script>
import OfferCode from "@/components/Common/OfferCode.vue"
import currencies from "@/consts/currencies"
import paginationConfigs from "@/consts/paginationConfigs"
import i18n from "@/i18n"
import { getCpaCampaignOrders } from "@/services/advertisingService.js"
import getPriceWithCurrency from "@/utils/getPriceWithCurrency"
import makeQuery from "@/utils/makeQuery"

const PAGE_SIZE = 10
const statusFilter = [
  {
    text: i18n.t("adversting.on_check"),
    value: "PENDING"
  },
  {
    text: i18n.t("adversting.decommissioned"),
    value: "APPLIED"
  },
  {
    text: i18n.t("adversting.returned"),
    value: "CANCELED"
  }
]

const createColumnsConfig = (options = {}) => {
  const { filteredInfo = {}, statusFilter = [] } = options

  return [
    {
      title: i18n.t("adversting.ordered_at"),
      dataIndex: "ordered_at",
      width: 95,
      scopedSlots: { customRender: "ordered_at" }
    },
    {
      title: i18n.t("adversting.order"),
      width: 100,
      dataIndex: "order_code"
    },
    {
      title: i18n.t("adversting.offer"),
      scopedSlots: { customRender: "offer" },
      dataIndex: "offer"
    },
    {
      title: i18n.t("adversting.quantity"),
      width: 60,
      dataIndex: "quantity"
    },
    {
      title: i18n.t("adversting.price"),
      dataIndex: "price_per_item",
      width: 120,
      customRender: (price) => getPriceWithCurrency(price, true)
    },
    {
      title: i18n.t("tariffs.cpa_statistics.commissionPercent"),
      dataIndex: "cpa.percent",
      width: 110
    },
    {
      title: i18n.t("tariffs.cpa_statistics.commissionSum", { currency: currencies.uk.label }),
      width: 120,
      dataIndex: "cpa.cost",
      customRender: (percent) => getPriceWithCurrency(percent)
    },
    {
      title: i18n.t("adversting.status"),
      width: 120,
      filters: statusFilter,
      filteredValue: filteredInfo.mod_status || null,
      filterMultiple: false,
      dataIndex: "cpa.mod_status",
      key: "mod_status",
      scopedSlots: { customRender: "status" }
    }
  ]
}

export default {
  name: "AdvertisingTable",

  components: {
    OfferCode,
    Thumbnail: () => import("@/components/Common/Thumbnail/Thumbnail.vue")
  },

  beforeCreate() {
    const initialPage = Number(this.$route.query.page)

    if (!initialPage) {
      const queryMixin = { ...this.$route.query, page: 1 }

      this.$router.replace({ query: queryMixin })
    }
  },
  created() {
    this.fetchPage(this.currentRoutePage)
  },
  data() {
    return {
      results: [],
      filteredInfo: {},
      fetching: false,
      pagination: {
        pageSize: PAGE_SIZE,
        current: 0,
        showQuickJumper: true,
        showSizeChanger: true,
        showTotal: paginationConfigs.default.showTotal,
        total: 0
      }
    }
  },
  computed: {
    columnsConfig() {
      return createColumnsConfig({ filteredInfo: this.filteredInfo, statusFilter })
    },
    currentRoutePage() {
      const page = Number(this.$route.query.page)
      const isValidPage = !isNaN(page) && page > 0

      return isValidPage ? page : 1
    }
  },
  methods: {
    handleTableChange(pagination, filters) {
      this.filteredInfo = filters
      this.pagination = pagination

      if (this.currentRoutePage !== pagination.current) {
        const queryMixin = { ...this.$route.query, page: pagination.current }

        this.$router.push({ query: queryMixin })
      }

      this.fetchPage(this.currentRoutePage)
    },
    getModStatusLabel(key) {
      if (key === "APPLIED") {
        return this.$t("adversting.decommissioned")
      } else if (key === "PENDING") {
        return this.$t("adversting.on_check")
      } else if (key === "CANCELED") {
        return this.$t("adversting.returned")
      }

      return ""
    },
    async fetchPage(page) {
      const query = makeQuery({ filters: this.filteredInfo })

      this.fetching = true

      try {
        const response = await getCpaCampaignOrders({
          pageSize: this.pagination.pageSize,
          page,
          query
        })
        const { results, count } = response.data

        this.results = results
        this.pagination.total = count
        this.pagination.current = page
      } finally {
        this.fetching = false
      }
    }
  }
}
</script>

<style lang="scss">
.table {
  &__offer {
    display: flex;
    align-items: center;
  }

  &__offer-thumbnail {
    width: 40px;
    height: 40px;
  }

  &__offer-description-wrapper {
    margin-left: 10px;
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  &__status,
  &__date {
    display: flex;
    flex-direction: column;
  }

  &__status-date,
  &__datetime {
    color: #8c8c8c;
  }
}
</style>
